import { farmDayKey } from './farmTime'
import { readFlowLpm } from './hatchery'

// Findings #211: when the crew must not feed a unit, and how much feed the
// unit's water can carry. Office-set per unit in `ProductionUnit.metadata.feedRules`
// (no migration; phones not yet updated ignore it).
//
// - Stop rules, from the unit's last reading of today: temperature outside
//   [tempMinC, tempMaxC] (the farm's hatchery: 8.5–14 °C), or inlet oxygen below
//   inletDoMinMgL (the farm's grow-out: 5.5 mg/L).
// - Oxygen capacity: feed the water supports (kg/day) =
//   caudal (L/min) × 1440 × (O2 entrada − mínimo a la salida) (mg/L) ÷ 10^6 ÷ kg O2 per kg feed
//   (Westers 1976: 0.25 kg O2 per kg feed, outlet ≥ 5.0 mg/L). Findings #219: the
//   suggested floor stays 5 mg/L at any altitude: mg/L follows the fish's need, and
//   altitude lowers saturation, not that need.

export type UnitFeedRules = {
  tempMinC?: number
  tempMaxC?: number
  inletDoMinMgL?: number
  outletDoMinMgL?: number
  o2KgPerKgFeed?: number
}

export const DEFAULT_O2_KG_PER_KG_FEED = 0.25
export const SUGGESTED_OUTLET_DO_MIN_MG_L = 5

type WithMetadata = { metadata?: Record<string, unknown> | null } | null | undefined

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
)

const finiteNumber = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : Number.NaN
  return Number.isFinite(parsed) ? parsed : undefined
}

const positive = (value: unknown): number | undefined => {
  const parsed = finiteNumber(value)
  return parsed !== undefined && parsed > 0 ? parsed : undefined
}

export const readFeedRules = (unit: WithMetadata): UnitFeedRules | null => {
  const raw = isPlainObject(unit?.metadata) ? unit?.metadata?.feedRules : undefined
  if (!isPlainObject(raw)) return null
  const rules: UnitFeedRules = {}
  const tempMinC = finiteNumber(raw.tempMinC)
  const tempMaxC = finiteNumber(raw.tempMaxC)
  if (tempMinC !== undefined) rules.tempMinC = tempMinC
  if (tempMaxC !== undefined && (tempMinC === undefined || tempMaxC > tempMinC)) rules.tempMaxC = tempMaxC
  const inletDoMinMgL = positive(raw.inletDoMinMgL)
  if (inletDoMinMgL !== undefined) rules.inletDoMinMgL = inletDoMinMgL
  const outletDoMinMgL = positive(raw.outletDoMinMgL)
  if (outletDoMinMgL !== undefined) rules.outletDoMinMgL = outletDoMinMgL
  const o2KgPerKgFeed = positive(raw.o2KgPerKgFeed)
  if (o2KgPerKgFeed !== undefined) rules.o2KgPerKgFeed = o2KgPerKgFeed
  return Object.keys(rules).length > 0 ? rules : null
}

export const hasStopRules = (rules: UnitFeedRules | null | undefined): boolean => Boolean(
  rules && (rules.tempMinC !== undefined || rules.tempMaxC !== undefined || rules.inletDoMinMgL !== undefined)
)

/** A water reading as the stop rules read it (office Water rows and Crew Mode rows alike). */
export type FeedRuleWaterRow = {
  date: string
  temperature: number | null
  dissolvedOxygen: number | null
  manualDissolvedOxygen?: number | null
  metadata?: unknown
}

/** `fromUnit`: the measuring point's name when the reading is not the unit's own (findings #217). */
export type LatestValue = { value: number; at: string; fromUnit?: string }

const doOf = (row: FeedRuleWaterRow): number | null => {
  const value = typeof row.dissolvedOxygen === 'number' ? row.dissolvedOxygen : row.manualDissolvedOxygen
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

const latestOf = (rows: FeedRuleWaterRow[], pick: (row: FeedRuleWaterRow) => number | null): LatestValue | null => {
  let best: LatestValue | null = null
  for (const row of rows) {
    const value = pick(row)
    if (value === null) continue
    const at = String(row.date ?? '')
    if (!best || at > best.at) best = { value, at }
  }
  return best
}

/** The unit's latest temperature and oxygen of `day` (farm day). */
export const latestReadingsOfDay = (rows: FeedRuleWaterRow[], day: string, timeZone: string): { temperature: LatestValue | null; dissolvedOxygen: LatestValue | null } => {
  const ofDay = rows.filter((row) => farmDayKey(row.date, timeZone) === day)
  return {
    temperature: latestOf(ofDay, (row) => (typeof row.temperature === 'number' && Number.isFinite(row.temperature) ? row.temperature : null)),
    dissolvedOxygen: latestOf(ofDay, doOf)
  }
}

/** The unit's latest inlet oxygen and caudal on any day (for the capacity estimate). */
export const latestOxygenAndFlow = (rows: FeedRuleWaterRow[]): { dissolvedOxygen: LatestValue | null; flowLpm: LatestValue | null } => ({
  dissolvedOxygen: latestOf(rows, doOf),
  flowLpm: latestOf(rows, (row) => readFlowLpm(row as { metadata?: unknown }))
})

export type FeedStopReason =
  | { kind: 'tempLow'; value: number; limit: number; at: string; fromUnit?: string }
  | { kind: 'tempHigh'; value: number; limit: number; at: string; fromUnit?: string }
  | { kind: 'doLow'; value: number; limit: number; at: string; fromUnit?: string }

export type FeedStopCheck = {
  /** At least one rule broken by today's last reading. */
  reasons: FeedStopReason[]
  /** Readings a rule needs that were not taken today. */
  missing: Array<'temperature' | 'dissolvedOxygen'>
}

const fromUnitOf = (reading: LatestValue) => (reading.fromUnit ? { fromUnit: reading.fromUnit } : {})

/** Findings #211: the stop rules against today's last readings (never blocks for a missing reading). */
export const checkFeedStop = (
  rules: UnitFeedRules | null | undefined,
  today: { temperature: LatestValue | null; dissolvedOxygen: LatestValue | null }
): FeedStopCheck => {
  const reasons: FeedStopReason[] = []
  const missing: FeedStopCheck['missing'] = []
  if (!rules) return { reasons, missing }
  if (rules.tempMinC !== undefined || rules.tempMaxC !== undefined) {
    const temperature = today.temperature
    if (!temperature) missing.push('temperature')
    else if (rules.tempMinC !== undefined && temperature.value < rules.tempMinC) {
      reasons.push({ kind: 'tempLow', value: temperature.value, limit: rules.tempMinC, at: temperature.at, ...fromUnitOf(temperature) })
    } else if (rules.tempMaxC !== undefined && temperature.value > rules.tempMaxC) {
      reasons.push({ kind: 'tempHigh', value: temperature.value, limit: rules.tempMaxC, at: temperature.at, ...fromUnitOf(temperature) })
    }
  }
  if (rules.inletDoMinMgL !== undefined) {
    const oxygen = today.dissolvedOxygen
    if (!oxygen) missing.push('dissolvedOxygen')
    else if (oxygen.value < rules.inletDoMinMgL) {
      reasons.push({ kind: 'doLow', value: oxygen.value, limit: rules.inletDoMinMgL, at: oxygen.at, ...fromUnitOf(oxygen) })
    }
  }
  return { reasons, missing }
}

/**
 * Findings #211: feed (kg/day) the unit's water can carry, or null without a
 * caudal, an inlet oxygen or an outlet floor; 0 when the inlet is at or below the floor.
 */
export const oxygenFeedCapacityKg = (args: {
  flowLpm: number | null
  inletDoMgL: number | null
  outletDoMinMgL: number | null
  o2KgPerKgFeed?: number | null
}): number | null => {
  const { flowLpm, inletDoMgL, outletDoMinMgL } = args
  const perKg = args.o2KgPerKgFeed && args.o2KgPerKgFeed > 0 ? args.o2KgPerKgFeed : DEFAULT_O2_KG_PER_KG_FEED
  if (flowLpm === null || inletDoMgL === null || outletDoMinMgL === null || flowLpm <= 0) return null
  const usableMgL = Math.max(0, inletDoMgL - outletDoMinMgL)
  const o2KgPerDay = (flowLpm * 1440 * usableMgL) / 1_000_000
  return o2KgPerDay / perKg
}
